import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, MapPin, Phone, User, Smartphone, Clock, Save } from 'lucide-react';
import { useSosLog } from 'features/sos-log/hooks/useSosLog';
import { useUpdateSosLogStatus } from 'features/sos-log/hooks/useUpdateSosLogStatus';
import { formatDateTime } from 'utils/dateFormatter';
import Layout from 'components/ui/Layout';
import { useState } from 'react';

const ViewSosLogPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: response, isLoading, isError, error } = useSosLog(id);
  const { mutate: updateStatus, isPending } = useUpdateSosLogStatus();

  const sosLog = response?.data?.sosLog;

  const [newStatus, setNewStatus] = useState('');

  const getStatusColor = (status) => {
    const colors = {
      triggered: 'bg-red-500/10 text-red-600 dark:text-red-400',
      acknowledged: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      resolved: 'bg-green-500/10 text-green-600 dark:text-green-400',
      cancelled: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getUserTypeColor = (type) => {
    const colors = {
      customer: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      seller: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    };
    return colors[type] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const handleStatusUpdate = () => {
    if (!newStatus) return;

    updateStatus(
      {
        sosLogId: sosLog._id,
        status: newStatus,
      },
      {
        onSuccess: () => {
          setNewStatus('');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-screen'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>Error loading SOS log</div>
          <p className='text-muted-foreground mb-4'>{error?.message || 'Something went wrong'}</p>
          <button onClick={() => navigate('/sos-logs')} className='px-4 py-2 bg-primary text-primary-foreground rounded-md'>
            Back to SOS Logs
          </button>
        </div>
      </Layout>
    );
  }

  if (!sosLog) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-lg mb-4'>SOS log not found</div>
          <button onClick={() => navigate('/sos-logs')} className='px-4 py-2 bg-primary text-primary-foreground rounded-md'>
            Back to SOS Logs
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='min-h-screen bg-background'>
        <div className='bg-card border-b border-border sticky top-0 z-10'>
          <div className='container mx-auto px-4 py-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-4'>
                <button
                  onClick={() => navigate('/sos-logs')}
                  className='p-2 hover:bg-muted rounded-md transition-colors'
                  title='Back to SOS logs'
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className='text-2xl font-bold flex items-center gap-2'>
                    <AlertTriangle className='text-red-500' size={24} />
                    SOS Log Details
                  </h1>
                  <p className='text-sm text-muted-foreground'>ID: {sosLog._id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='container mx-auto px-4 py-6'>
          <div className='bg-card border border-border rounded-lg p-6 space-y-6'>
            {/* Status Badge */}
            <div className='flex flex-wrap gap-2'>
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(sosLog.status)}`}>
                {sosLog.status?.replace(/_/g, ' ')?.toUpperCase()}
              </span>
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getUserTypeColor(sosLog.userType)}`}>
                {sosLog.userType?.toUpperCase()}
              </span>
              <span className='px-3 py-1 text-xs font-medium rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400'>
                {sosLog.alertType?.toUpperCase()}
              </span>
            </div>

            {/* User Information */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='space-y-2 bg-muted rounded-lg p-4'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <User size={14} />
                  <span>User Information</span>
                </label>
                <div className='flex items-center space-x-3'>
                  {sosLog.user?.profilePhoto ? (
                    <img
                      src={sosLog.user.profilePhoto}
                      alt={`${sosLog.user.firstName} ${sosLog.user.lastName}`}
                      className='w-12 h-12 rounded-full object-cover border border-border'
                    />
                  ) : (
                    <div className='w-12 h-12 rounded-full bg-primary/10 border border-border flex items-center justify-center'>
                      <User size={24} />
                    </div>
                  )}
                  <div>
                    <p className='text-sm font-medium'>
                      {sosLog.user?.firstName} {sosLog.user?.lastName}
                    </p>
                    <p className='text-xs text-muted-foreground'>{sosLog.user?.email}</p>
                    <p className='text-xs text-muted-foreground flex items-center gap-1'>
                      <Phone size={10} />
                      {sosLog.user?.countryCode} {sosLog.user?.phoneNumber}
                    </p>
                  </div>
                </div>
              </div>

            <div className='space-y-2 bg-muted rounded-lg p-4'>
  <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
    <Phone size={14} />
    <span>Emergency Contacts</span>
  </label>

  {sosLog.emergencyContacts?.length ? (
    <div className='space-y-3'>
      {sosLog.emergencyContacts.map((contact) => (
        <div
          key={contact._id}
          className='border border-border rounded-md p-3 bg-background'
        >
          <p className='text-sm font-medium'>
            {contact.name || 'N/A'}
          </p>

          <p className='text-xs text-muted-foreground flex items-center gap-1 mt-1'>
            <Phone size={10} />
            {contact.countryCode} {contact.phoneNumber}
          </p>

          <p className='text-xs text-muted-foreground mt-1'>
            Relationship: {contact.relationship || 'N/A'}
          </p>

          {contact.isPrimary && (
            <span className='inline-flex mt-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded font-medium'>
              Primary Contact
            </span>
          )}
        </div>
      ))}
    </div>
  ) : (
    <p className='text-sm text-muted-foreground'>
      No emergency contacts found
    </p>
  )}
</div>
            </div>

            {/* Location */}
            <div className='space-y-2 bg-muted rounded-lg p-4'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <MapPin size={14} />
                <span>Location</span>
              </label>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium'>Coordinates:</span>
                  <span className='text-sm text-muted-foreground'>
                    Lat: {sosLog.location?.coordinates?.[1]?.toFixed(6)}, Long: {sosLog.location?.coordinates?.[0]?.toFixed(6)}
                  </span>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${sosLog.location?.coordinates?.[1]},${sosLog.location?.coordinates?.[0]}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center gap-1 text-sm text-primary hover:underline'
                >
                  <MapPin size={14} />
                  Open in Google Maps
                </a>
              </div>
            </div>

            {/* Device Information */}
            <div className='space-y-2 bg-muted rounded-lg p-4'>
              <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                <Smartphone size={14} />
                <span>Device Information</span>
              </label>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-3 text-sm'>
                <div>
                  <span className='text-muted-foreground text-xs'>Platform:</span>
                  <p className='font-medium'>{sosLog.deviceInfo?.platform?.toUpperCase() || 'N/A'}</p>
                </div>
                <div>
                  <span className='text-muted-foreground text-xs'>Device Type:</span>
                  <p className='font-medium'>{sosLog.deviceInfo?.deviceType || 'N/A'}</p>
                </div>
                <div>
                  <span className='text-muted-foreground text-xs'>OS:</span>
                  <p className='font-medium'>{sosLog.deviceInfo?.os || 'N/A'}</p>
                </div>
                <div>
                  <span className='text-muted-foreground text-xs'>App Version:</span>
                  <p className='font-medium'>{sosLog.deviceInfo?.appVersion || 'N/A'}</p>
                </div>
              </div>
              {sosLog.deviceInfo?.deviceId && (
                <div className='mt-2'>
                  <span className='text-muted-foreground text-xs'>Device ID:</span>
                  <p className='font-medium text-xs'>{sosLog.deviceInfo.deviceId}</p>
                </div>
              )}
            </div>

            {/* Notes */}
            {sosLog.notes && (
              <div className='space-y-2 bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4'>
                <label className='text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase'>Notes</label>
                <p className='text-sm'>{sosLog.notes}</p>
              </div>
            )}

            {/* Timestamps */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border'>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Clock size={14} />
                  <span>Created At</span>
                </label>
                <p className='text-sm'>{formatDateTime(sosLog.createdAt)}</p>
              </div>

              <div className='space-y-1'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <Clock size={14} />
                  <span>Last Updated</span>
                </label>
                <p className='text-sm'>{formatDateTime(sosLog.updatedAt)}</p>
              </div>
            </div>

            {/* Update Status Section SOS */}
            {/* <div className='border-t border-border pt-6 space-y-4'>
              <h3 className='text-lg font-semibold'>Update Status</h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium mb-2'>New Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
                  >
                    <option value=''>Select status...</option>
                    <option value='triggered'>Triggered</option>
                    <option value='acknowledged'>Acknowledged</option>
                    <option value='resolved'>Resolved</option>
                    <option value='cancelled'>Cancelled</option>
                  </select>
                </div>

                <div className='flex items-end'>
                  <button
                    onClick={handleStatusUpdate}
                    disabled={!newStatus || isPending}
                    className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2'
                  >
                    {isPending ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white' />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        <span>Update Status</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ViewSosLogPage;
